import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import { useSearch } from 'wouter-preact'
import { Seite } from '../app.jsx'
import { api } from '../lib/api.js'
import { KartenMarkdown } from '../lib/markdown.jsx'
import { BildKarte } from '../components/BildKarte.jsx'
import { Laden, Fehler, Leer } from '../components/Zustand.jsx'

/**
 * Freier Modus: durchblättern ohne Bewertung.
 *
 * Nutzt ausschließlich die Verwaltungsrouten — es wird kein Lernstand
 * angefasst, keine Bewertung gesendet und keine Fälligkeit verändert.
 *
 * Das Thema steht als Auswahlfeld über der Karte, wie in der Verwaltung
 * (DESIGN.md, Dichte „komfortabel"): Navigation ist hier der Zweck.
 *
 * Einstieg ist eine Übersicht aller Vorderseiten mit Suche. Ein Tipp springt
 * zur Karte — bei 50 Ländern je Kontinent dauert Durchblättern zu lange.
 *
 * „Rückwärts“ zeigt zuerst die Rückseite, wie die Rückwärts-Abfrage beim
 * Lernen — nur für normale Karten mit beiden Seiten, sonst bleibt es vorwärts.
 */

const RICHTUNG_SCHLUESSEL = 'blaettern-richtung'

function gemerkteRichtung() {
  try {
    return localStorage.getItem(RICHTUNG_SCHLUESSEL) === 'rueckwaerts'
  } catch {
    return false
  }
}

function merkeRichtung(rueckwaerts) {
  try {
    if (rueckwaerts) localStorage.setItem(RICHTUNG_SCHLUESSEL, 'rueckwaerts')
    else localStorage.removeItem(RICHTUNG_SCHLUESSEL)
  } catch {
    // Ohne Speicher gilt die Wahl eben nur bis zum Verlassen der Seite.
  }
}

/** Gleiche Regel wie `istUmkehrbar` auf dem Server. */
function umkehrbar(karte) {
  return karte.card_type === 'normal' && Boolean(karte.front?.trim()) && Boolean(karte.back?.trim())
}

export function Blaettern() {
  const suche = useSearch()
  const prefixAusAdresse = new URLSearchParams(suche).get('prefix') || ''

  const [prefix, setPrefix] = useState(prefixAusAdresse)
  const [liste, setListe] = useState(null)
  const [ordner, setOrdner] = useState([])
  const [index, setIndex] = useState(0)
  const [ansicht, setAnsicht] = useState('uebersicht')
  const [frage, setFrage] = useState('')
  const [rueckwaerts, setRueckwaerts] = useState(gemerkteRichtung)
  const [karte, setKarte] = useState(null)
  const [umgedreht, setUmgedreht] = useState(false)
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState(null)

  useEffect(() => {
    setLaedt(true)
    api
      .get('/api/cards', { prefix })
      .then((daten) => {
        setListe(daten.cards ?? [])
        setOrdner(daten.folders ?? [])
        setKarte(null)
        setIndex(0)
        setAnsicht('uebersicht')
        setLaedt(false)
      })
      .catch((f) => {
        setFehler(f)
        setLaedt(false)
      })
  }, [prefix])

  // Karteninhalt zur aktuellen Stelle nachladen.
  useEffect(() => {
    const eintrag = liste?.[index]
    if (!eintrag || ansicht !== 'karte') return undefined
    let abgebrochen = false
    setUmgedreht(false)
    api
      .get('/api/cards/one', { id: eintrag.id })
      .then((daten) => {
        if (!abgebrochen) setKarte(daten)
      })
      .catch((f) => {
        if (!abgebrochen) setFehler(f)
      })
    return () => {
      abgebrochen = true
    }
  }, [liste, index, ansicht])

  const blaettern = useCallback(
    (richtung) => {
      setIndex((i) => {
        const ziel = i + richtung
        if (!liste || ziel < 0 || ziel >= liste.length) return i
        return ziel
      })
    },
    [liste],
  )

  // Wie im Lernbildschirm: einmal registrieren, aktuelle Funktion über Ref.
  const aktion = useRef(blaettern)
  aktion.current = blaettern
  const ansichtRef = useRef(ansicht)
  ansichtRef.current = ansicht

  useEffect(() => {
    function beiTaste(ereignis) {
      const ziel = ereignis.target
      if (ziel instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(ziel.tagName)) return
      if (ansichtRef.current !== 'karte') return
      if (ereignis.key === 'ArrowRight') aktion.current(1)
      else if (ereignis.key === 'ArrowLeft') aktion.current(-1)
      else if (ereignis.key === ' ' || ereignis.key === 'Enter') {
        ereignis.preventDefault()
        setUmgedreht((u) => !u)
      }
    }
    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [])

  if (fehler) return <Fehler fehler={fehler} />

  // Das Auswahlfeld steht über jedem Zustand — sonst säße man in einem leeren
  // Thema fest und käme nur über die Adresszeile wieder heraus.
  const themenwahl = (
    <label class="feld blaettern__filter">
      <span class="feld__label meta">Thema</span>
      <select
        class="feld__eingabe"
        value={prefix}
        onChange={(e) => setPrefix(e.currentTarget.value)}
      >
        <option value="">Alle Themen</option>
        {ordner.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  )

  if (laedt && !liste) {
    return (
      <Seite titel="Blättern">
        {themenwahl}
        <Laden />
      </Seite>
    )
  }

  if (!liste?.length) {
    return (
      <Seite titel="Blättern">
        {themenwahl}
        <Leer
          text="In diesem Thema gibt es keine Karten."
          kind={
            <a class="knopf knopf--still" href="/">
              Zur Themenliste
            </a>
          }
        />
      </Seite>
    )
  }

  if (ansicht === 'uebersicht') {
    // Rückwärts stehen die Rückseiten auf den Kacheln — so, wie die Karte
    // gleich gefragt wird. Nicht umkehrbare Karten zeigen weiter ihre Vorderseite.
    const aufschrift = (eintrag) =>
      (rueckwaerts && eintrag.back_preview) || eintrag.front_preview || eintrag.title
    const gesucht = frage.trim().toLowerCase()
    const treffer = liste
      .map((eintrag, i) => ({ eintrag, i }))
      .filter(({ eintrag }) => !gesucht || aufschrift(eintrag).toLowerCase().includes(gesucht))

    // Bei „Alle Themen“ nach Thema gruppiert, sonst eine einzige Gruppe.
    const gruppen = []
    for (const t of treffer) {
      const letzte = gruppen[gruppen.length - 1]
      if (letzte && letzte.ordner === t.eintrag.folder) letzte.karten.push(t)
      else gruppen.push({ ordner: t.eintrag.folder, karten: [t] })
    }

    return (
      <Seite titel="Blättern">
        <div class="blaettern__kopf">
          <label class="feld blaettern__suche">
            <span class="feld__label meta">Suche</span>
            <input
              class="feld__eingabe"
              type="search"
              value={frage}
              onInput={(e) => setFrage(e.currentTarget.value)}
              placeholder={rueckwaerts ? 'Rückseiten durchsuchen' : 'Vorderseiten durchsuchen'}
            />
          </label>
          {themenwahl}
          <label class="feld blaettern__richtung">
            <span class="feld__label meta">Richtung</span>
            <select
              class="feld__eingabe"
              value={rueckwaerts ? 'rueckwaerts' : 'vorwaerts'}
              onChange={(e) => {
                const r = e.currentTarget.value === 'rueckwaerts'
                setRueckwaerts(r)
                merkeRichtung(r)
              }}
            >
              <option value="vorwaerts">Vorwärts</option>
              <option value="rueckwaerts">Rückwärts</option>
            </select>
          </label>
        </div>

        {treffer.length ? (
          gruppen.map((g) => (
            <section key={g.ordner} class="blaettern__gruppe">
              {prefix ? null : <h2 class="meta blaettern__ordner">{g.ordner || 'Ohne Thema'}</h2>}
              <ul class="blaettern__raster">
                {g.karten.map(({ eintrag, i }) => (
                  <li key={eintrag.id}>
                    <button
                      type="button"
                      class="blaettern__kachel"
                      onClick={() => {
                        setIndex(i)
                        setAnsicht('karte')
                      }}
                    >
                      <span class="blaettern__kachel-text">
                        {aufschrift(eintrag)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        ) : (
          <Leer text="Keine Karte passt zur Suche." />
        )}
      </Seite>
    )
  }

  const gedreht = Boolean(karte) && rueckwaerts && umkehrbar(karte)
  const vorne = gedreht ? karte.back : karte?.front
  const hinten = gedreht ? karte.front : karte?.back

  return (
    <Seite titel="Blättern">
      <div class="blaettern__leiste">
        <button
          type="button"
          class="knopf knopf--still"
          onClick={() => {
            setKarte(null)
            setAnsicht('uebersicht')
          }}
        >
          ← Übersicht
        </button>
        <p class="meta blaettern__zaehler zahl">
          {index + 1} von {liste.length}
        </p>
      </div>

      <div
        class="blaettern__karte"
        role="button"
        tabIndex={0}
        aria-label="Karte umdrehen"
        onClick={() => setUmgedreht((u) => !u)}
      >
        {karte ? (
          karte.card_type === 'io' ? (
            <BildKarte karte={karte} umgedreht={umgedreht} />
          ) : (
          <div class={gedreht ? 'kartentext--rueckwaerts' : undefined}>
            {gedreht ? (
              <>
                <span class="visuell-versteckt">Rückwärts gefragt.</span>
                <span class="kartentext__richtung" aria-hidden="true">
                  ↻
                </span>
              </>
            ) : null}
            <KartenMarkdown class="kartentext__frage" text={vorne} />
            {umgedreht ? (
              <div class="kartentext__antwort-block">
                <hr class="kartentext__trenner" />
                <KartenMarkdown class="kartentext__antwort" text={hinten} />
                {karte.back_extra ? (
                  <KartenMarkdown class="kartentext__zusatz" text={karte.back_extra} />
                ) : null}
              </div>
            ) : null}
          </div>
          )
        ) : (
          <Laden />
        )}
      </div>

      <div class="blaettern__navigation">
        <button
          type="button"
          class="knopf knopf--still"
          disabled={index === 0}
          onClick={() => blaettern(-1)}
        >
          ← Zurück
        </button>
        <button
          type="button"
          class="knopf knopf--still"
          disabled={index >= liste.length - 1}
          onClick={() => blaettern(1)}
        >
          Weiter →
        </button>
      </div>
    </Seite>
  )
}
